import { ComponentType, ReactNode, useContext, useEffect } from 'react';
import AppConfig from 'config/AppConfig';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthContext, IAgxConfig } from '@urbanx/agx.auth.client';
import LogRocket from 'logrocket';
import ApiClients from './ApiClients';

export const createStaffQueryClient = () => {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: Infinity,
        gcTime: Infinity,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        retry: 1,
        retryDelay: 3000,
        networkMode: 'always',
      },
    },
    queryCache: new QueryCache({
      onError: (error) => {
        console.error(error);
      },
    }),
  });

  queryClient.setQueryDefaults(['getDashboardStats'], {
    staleTime: 0,
    gcTime: 0,
  });

  queryClient.setQueryDefaults(['getMigrations'], {
    refetchOnMount: 'always',
  });

  return queryClient;
};

const queryClient = createStaffQueryClient();

export interface ProviderProps {
  children: ReactNode;
  appConfig: IAgxConfig;
}

const combineProviders = (providers: ComponentType<ProviderProps>[]) => {
  return function CombinedProviders({ children }: { children: ReactNode }) {
    const { userAccount } = useContext(AuthContext);

    useEffect(() => {
      LogRocket.identify(userAccount.agent || '', {
        name: userAccount.fullName || '',
        email: userAccount.agent || '',
        agency: userAccount.agency || '',
      });
    }, [userAccount]);

    return providers.reduce(
      (acc, Provider) => <Provider appConfig={AppConfig}>{acc}</Provider>,
      children
    );
  };
};

const CombinedProviders = combineProviders(ApiClients);

const ApiProviders = ({ children }: { children: ReactNode }) => {
  return (
    <QueryClientProvider client={queryClient}>
      <CombinedProviders>{children}</CombinedProviders>
    </QueryClientProvider>
  );
};

export default ApiProviders;
